<template>
  <Highcharts
    ref="chart"
    :options="chartOptions"
  />
</template>

<script>
import { parseISO, getTime } from 'date-fns';
import { Chart } from 'highcharts-vue';

import { formatterDateLabel, formatterTooltip } from './chart-helper.js';
import { formatNumberWithSuffixesCompact } from '@/utils/format-number-with-suffixes-compact';

export default {
  name: 'AreaSplineAndStackedColumn',
  components: {
    Highcharts: Chart,
  },
  props: {
    series: {
      type: Array,
      required: true,
    },
    categories: {
      type: String,
      required: true,
    },
    overrideChartOptions: {
      type: Object,
      default: () => ({}),
    },
    verticalTextLeft: {
      type: String,
      required: true,
    },
    verticalTextRight: {
      type: String,
      required: true,
    },
  },
  computed: {
    chartOptions() {
      const categories = this.categories;
      const verticalTextLeft = this.verticalTextLeft;
      const verticalTextRight = this.verticalTextRight;
      const getMiliSecondsTimeStamp = (date) => getTime(parseISO(date));

      const series = this.series.map(serie => {
        return {
          ...serie,
          data: serie.data.map(dataItem => ({
            ...dataItem,
            x: getMiliSecondsTimeStamp(dataItem.date),
            y: dataItem.y,
          })),
        };
      });

      return {
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        legend: {
          align: 'left',
          margin: 18,
          padding: 0,
          reversed: true,
        },
        lang: {
          decimalPoint: ',',
          thousandsSep: '.',
        },
        tooltip: {
          shared: true,
          useHTML: true,
          shadow: false,
          borderRadius: 4,
          formatter() {
            return formatterTooltip(this, categories);
          },
        },
        chart: {
          type: 'areaspline',
          styleMode: true,
          borderRadius: 6,
          spacingBottom: 3,
          spacingLeft: 0,
          spacingRight: 0,
          events: {
            load() {
              this.update({
                xAxis: {
                  tickPositioner() {
                    const chart = this.chart,
                      tickPositions = [];

                    chart.series[0].data.forEach((point) => {
                      tickPositions.push(point.x);
                    });

                    this.update({
                      tickPositions,
                    });
                  },
                },
              });
            },
          },
        },
        xAxis: {
          type: 'datetime',
          startOfWeek: 0,
          tickWidth: 0,
          crosshair: true,
          labels: {
            formatter() {
              return formatterDateLabel(this.value);
            },
            y: 38,
            style: {
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: 16,
              color: 'var(--ds-color-text-2)',
            },
          },
        },
        yAxis: [
          {
            title: {
              text: verticalTextLeft,
              style: {
                color: 'var(--ds-color-text-1)',
                fontSize: 'var(--ds-font-size-xs)',
                fontWeight: 'var(--ds-font-weight-semibold)',
              },
            },
            labels: {
              formatter() {
                return formatNumberWithSuffixesCompact(this.value);
              },
            },
          },
          {
            title: {
              text: verticalTextRight,
              style: {
                color: 'var(--ds-color-text-1)',
                fontSize: 'var(--ds-font-size-xs)',
                fontWeight: 'var(--ds-font-weight-semibold)',
              },
            },
            labels: {
              formatter() {
                return formatNumberWithSuffixesCompact(this.value);
              },
            },
            opposite: true,
          },
        ],
        series,
        plotOptions: {
          series: {
            fillOpacity: 0,
            stacking: 'normal',
            cursor: 'pointer',
            marker: {
              enabled: false,
              symbol: 'circle',
            },
            events: {
              legendItemClick: (e) => {
                this.$emit('legend-click', { legend: e.target.name });
              },
              mouseOver() {
                const series = this.chart.series;
                series.forEach((item) => {
                  if (item !== this && item.initialType !== 'spline') {
                    item.update({
                      opacity: 0.2,
                    }, false);
                  }
                });
                this.chart.redraw();
              },
              mouseOut () {
                const series = this.chart.series;
                series.forEach((item) => {
                  if (item !== this) {
                    item.update({
                      opacity: 1,
                    }, false);
                  }
                });
                this.chart.redraw();
              },
            },
          },
        },
        ...this.overrideChartOptions,
      };
    },
  },
};
</script>

<style scoped lang='scss'>
::v-deep {
  .highcharts {
    &-tooltip {
      border-radius: var(--ds-border-radius-sm);
    }
  }

  .highcharts-custom-tooltip {
    box-shadow: var(--ds-light-dropshadow-1);
    margin: 0;
    padding: var(--ds-spacing-stack-xs);
    border-radius: var(--ds-border-radius-sm);
    background: var(--ds-color-neutral);

    &__header {
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-xs);
      margin-bottom: var(--ds-spacing-stack-xs);
      font-weight: var(--ds-font-weight-regular);
    }

    &__content {
      line-height: 25px;
      display: flex;
      flex-direction: column-reverse;
    }

    &__item-wrapper {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 36px;
      justify-content: space-between;
    }

    &__item span {
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-xs);
      font-weight: var(--ds-font-weight-regular);
    }

    &__column {
      &::before {
        content: '';
        display: inline-block;
        padding: var(--ds-spacing-stack-quark);
        margin-right: 10px;
        border-radius: var(--ds-border-radius-pill);
        background-color: currentColor;
      }
    }

    &__spline {
      &::before {
        content: '';
        display: inline-block;
        width: 11px;
        height: 2px;
        margin-right: var(--ds-spacing-stack-nano);
        background: var(--ds-color-reverse);
      }
    }

    &__variation {
      color: var(--ds-color-text-1);
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xs);
      text-align: right;
    }
  }
}
</style>
